import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FiChevronRight, FiChevronDown, FiPlus, FiEdit2, FiAlertTriangle, FiInfo } from 'react-icons/fi';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';
import PlantillaModal from '../util/PlantillaModal';
import SpinnerCargando from '../util/SpinnerCargando';

// =====================================================
// CONSTANTES
// =====================================================
const ESTADOS = {
  ACTIVA: 1,
  AGOTADA: 2,
  VENCIDA: 3,
  INACTIVA: 4,
  ANULADA: 5
};

// =====================================================
// COMPONENTE: Fila de Grupo (Stack por Prefijo)
// =====================================================
function FilaGrupo({ grupo, expandido, onToggle, onEditar, onCambiarEstado }) {
  const { prefijo, tipo_descripcion, es_electronica, enUso, enCola, alerta, siguienteNCF, totalEnCola } = grupo;
  
  const rangoInicio = enUso?.contador_inicial || enUso?.contador || 1;
  const rangoFin = enUso?.fin;
  const disponibles = enUso?.disponibles || 0;

  return (
    <div className="border-b border-gray-200 last:border-b-0">
      {/* Fila Principal */}
      <div 
        className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${expandido ? 'bg-gray-50' : ''}`}
        onClick={onToggle}
      >
        {/* Expandir/Colapsar */}
        <button className="text-gray-400 hover:text-gray-600">
          {expandido ? <FiChevronDown size={18} /> : <FiChevronRight size={18} />}
        </button>
        
        {/* Prefijo y Tipo */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono font-semibold text-gray-800">{prefijo}</span>
            <span className="text-gray-500">·</span>
            <span className="text-gray-600 truncate">{tipo_descripcion}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded ${es_electronica ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
              {es_electronica ? 'E' : 'B'}
            </span>
          </div>
          {enUso && (
            <div className="text-sm text-gray-500 mt-0.5">
              Siguiente: <span className="font-mono">{siguienteNCF}</span>
              {totalEnCola > 0 && (
                <span className="ml-2 text-color-secundario">+{totalEnCola} en cola</span>
              )}
            </div>
          )}
        </div>
        
        {/* Disponibles y Alerta */}
        <div className="flex items-center gap-4">
          {alerta && (
            <div className={`flex items-center gap-1 text-sm ${
              alerta.nivel === 'critico' ? 'text-red-600' :
              alerta.nivel === 'warning' ? 'text-amber-600' :
              'text-gray-500'
            }`}>
              {alerta.nivel === 'warning' || alerta.nivel === 'critico' ? (
                <FiAlertTriangle size={14} />
              ) : (
                <FiInfo size={14} />
              )}
              <span>{alerta.mensaje}</span>
            </div>
          )}
          
          {enUso && !alerta && (
            <span className="text-sm text-gray-500">
              {disponibles.toLocaleString()} disponibles
            </span>
          )}
          
          {/* Botón Editar */}
          <button
            onClick={(e) => { e.stopPropagation(); onEditar(enUso || enCola[0]); }}
            className="p-2 text-gray-400 hover:text-color-primario hover:bg-gray-100 rounded-lg transition-colors"
            title="Editar"
          >
            <FiEdit2 size={16} />
          </button>
        </div>
      </div>
      
      {/* Panel Expandido */}
      {expandido && (
        <div className="px-4 pb-4 pt-2 bg-gray-50">
          {/* Secuencia en Uso */}
          {enUso && (
            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-3">
              <div className="flex justify-between items-start mb-3">
                <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded">EN USO</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => onEditar(enUso)}
                    className="text-xs px-3 py-1 border border-color-primario text-color-primario rounded-lg hover:bg-gray-50"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => onCambiarEstado(enUso, ESTADOS.INACTIVA)}
                    className="text-xs px-3 py-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  >
                    Desactivar
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Siguiente NCF</span>
                  <p className="font-mono font-medium">{siguienteNCF}</p>
                </div>
                <div>
                  <span className="text-gray-500">Rango</span>
                  <p className="font-mono">{rangoInicio?.toLocaleString()} - {rangoFin?.toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-gray-500">Disponibles</span>
                  <p className={disponibles < (enUso.alerta || 1000) ? 'text-amber-600 font-semibold' : ''}>
                    {disponibles.toLocaleString()}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">Vence</span>
                  <p>{enUso.fecha_vencimiento}</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Secuencias en Cola */}
          {enCola.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase mb-2">En Cola ({enCola.length})</p>
              <div className="space-y-2">
                {enCola.map((sec, idx) => (
                  <div key={sec.id_secuencia_comprobante} className="flex items-center justify-between bg-white rounded-lg border border-gray-200 px-4 py-2 text-sm">
                    <div className="flex items-center gap-4">
                      <span className="text-gray-400 w-5">{idx + 1}.</span>
                      <span className="font-mono text-gray-600">
                        {(sec.contador_inicial || sec.contador || 1).toLocaleString()} - {sec.fin?.toLocaleString()}
                      </span>
                      <span className="text-gray-400">·</span>
                      <span className="text-gray-500">{sec.disponibles?.toLocaleString()} NCFs</span>
                      <span className="text-gray-400">·</span>
                      <span className="text-gray-500">Vence: {sec.fecha_vencimiento}</span>
                    </div>
                    <button
                      onClick={() => onEditar(sec)}
                      className="text-gray-400 hover:text-color-primario"
                    >
                      <FiEdit2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Agregar nueva secuencia a este prefijo */}
          <button
            onClick={() => onEditar({ prefijo, id_tipo_comprobante_fiscal: grupo.id_tipo_comprobante_fiscal, es_electronica })}
            className="mt-3 flex items-center gap-2 text-sm text-color-secundario hover:text-color-primario"
          >
            <FiPlus size={14} />
            Agregar secuencia a {prefijo}
          </button>
        </div>
      )}
    </div>
  );
}

// =====================================================
// COMPONENTE: Serie Sin Configurar
// =====================================================
function SerieSinConfigurar({ tipo, serie, esElectronica, onConfigurar }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 last:border-b-0">
      <div className="flex items-center gap-3">
        <div className={`w-2 h-2 rounded-full ${esElectronica ? 'bg-blue-400' : 'bg-amber-400'}`}></div>
        <span className="text-gray-600">{tipo.descripcion}</span>
        <span className="font-mono text-sm text-gray-700">{serie}</span>
        <span className={`text-xs px-1.5 py-0.5 rounded ${esElectronica ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
          {esElectronica ? 'Electrónica' : 'Física'}
        </span>
      </div>
      <button
        onClick={() => onConfigurar(tipo, serie, esElectronica)}
        className="flex items-center gap-1 text-sm text-color-secundario hover:text-color-primario"
      >
        <FiPlus size={14} />
        Configurar
      </button>
    </div>
  );
}

// =====================================================
// COMPONENTE: Modal de Confirmación
// =====================================================
function ModalConfirmacion({ mostrar, titulo, mensaje, onConfirmar, onCancelar, peligroso }) {
  if (!mostrar) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl">
        <h3 className="text-lg font-semibold mb-2">{titulo}</h3>
        <p className="text-gray-600 text-sm mb-4">{mensaje}</p>
        <div className="flex gap-3">
          <button
            onClick={onCancelar}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 text-sm"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirmar}
            className={`flex-1 px-4 py-2 rounded-xl text-white text-sm ${
              peligroso ? 'bg-red-600 hover:bg-red-700' : 'bg-color-primario hover:bg-color-secundario'
            }`}
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================
// COMPONENTE: Modal de Edición/Creación
// =====================================================
function ModalEdicion({ mostrar, onClose, secuencia, tipos, onGuardar }) {
  const [form, setForm] = useState({
    id_tipo_comprobante_fiscal: '',
    es_electronica: true,
    prefijo: '',
    contador: 1,
    fin: 10000000,
    fecha_vencimiento: '',
    autorizacion: '',
    alerta: 1000
  });
  const [guardando, setGuardando] = useState(false);
  const [mostrarConfirmacion, setMostrarConfirmacion] = useState(false);

  useEffect(() => {
    if (secuencia) {
      if (secuencia.id_secuencia_comprobante) {
        // Editar secuencia existente
        setForm({
          id_secuencia_comprobante: secuencia.id_secuencia_comprobante,
          id_tipo_comprobante_fiscal: secuencia.id_tipo_comprobante_fiscal,
          es_electronica: secuencia.es_electronica,
          prefijo: secuencia.prefijo,
          contador: secuencia.contador,
          contador_inicial: secuencia.contador_inicial,
          fin: secuencia.fin,
          fecha_vencimiento: secuencia.fecha_vencimiento,
          autorizacion: secuencia.autorizacion || '',
          alerta: secuencia.alerta || 1000,
          en_uso: secuencia.en_uso
        });
      } else {
        // Nueva secuencia con prefijo predefinido (desde "Agregar a este prefijo")
        setForm({
          id_tipo_comprobante_fiscal: secuencia.id_tipo_comprobante_fiscal || '',
          es_electronica: secuencia.es_electronica ?? true,
          prefijo: secuencia.prefijo || '',
          contador: 1,
          fin: 10000000,
          fecha_vencimiento: '',
          autorizacion: '',
          alerta: 1000
        });
      }
    } else {
      // Nueva secuencia desde cero
      setForm({
        id_tipo_comprobante_fiscal: '',
        es_electronica: true,
        prefijo: '',
        contador: 1,
        fin: 10000000,
        fecha_vencimiento: '',
        autorizacion: '',
        alerta: 1000
      });
    }
  }, [secuencia, mostrar]);

  // Auto-generar prefijo al seleccionar tipo
  useEffect(() => {
    if (!secuencia?.id_secuencia_comprobante && !secuencia?.prefijo && form.id_tipo_comprobante_fiscal) {
      const tipo = tipos.find(t => t.id_tipo_comprobante_fiscal === parseInt(form.id_tipo_comprobante_fiscal));
      if (tipo) {
        const prefijo = form.es_electronica ? tipo.serie_electronica : tipo.serie_fisica;
        setForm(prev => ({ ...prev, prefijo: prefijo || '' }));
      }
    }
  }, [form.id_tipo_comprobante_fiscal, form.es_electronica, tipos, secuencia]);

  const validar = () => {
    if (!form.prefijo) {
      toast.error('Seleccione un tipo de comprobante');
      return false;
    }
    if (!form.fecha_vencimiento) {
      toast.error('La fecha de vencimiento es requerida');
      return false;
    }
    if (!form.id_secuencia_comprobante && (form.contador < 1 || !form.contador)) {
      toast.error('El contador inicial debe ser al menos 1');
      return false;
    }
    if (form.fin < (form.contador || 1)) {
      toast.error('El fin no puede ser menor que el contador');
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validar()) return;
    setMostrarConfirmacion(true);
  };

  const confirmarGuardar = async () => {
    setMostrarConfirmacion(false);
    setGuardando(true);
    try {
      await onGuardar(form);
      onClose();
    } catch (error) {
      // Error manejado en onGuardar
    } finally {
      setGuardando(false);
    }
  };

  const esEdicion = !!secuencia?.id_secuencia_comprobante;
  const enUso = secuencia?.en_uso;
  const prefijoFijo = !!secuencia?.prefijo;

  return (
    <>
      <PlantillaModal mostrar={mostrar} onClose={onClose} maxWidth="max-w-md">
        <div>
          <h2 className="text-lg font-semibold mb-4">
            {esEdicion ? `Editar Secuencia ${form.prefijo}` : prefijoFijo ? `Nueva Secuencia ${form.prefijo}` : 'Nueva Secuencia'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Tipo de Comprobante - solo si es nueva sin prefijo */}
            {!esEdicion && !prefijoFijo && (
              <>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Tipo de Comprobante *</label>
                  <select
                    value={form.id_tipo_comprobante_fiscal}
                    onChange={(e) => setForm(prev => ({ ...prev, id_tipo_comprobante_fiscal: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2"
                    required
                  >
                    <option value="">Seleccione...</option>
                    {tipos.map(t => (
                      <option key={t.id_tipo_comprobante_fiscal} value={t.id_tipo_comprobante_fiscal}>
                        {t.descripcion}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-2">Tipo de Serie *</label>
                  <div className="flex gap-3">
                    <label className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 cursor-pointer transition-colors ${
                      form.es_electronica ? 'border-color-primario bg-green-50' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input
                        type="radio"
                        checked={form.es_electronica}
                        onChange={() => setForm(prev => ({ ...prev, es_electronica: true }))}
                        className="sr-only"
                      />
                      <span className={form.es_electronica ? 'text-color-primario font-medium' : 'text-gray-600'}>
                        Electrónica (E)
                      </span>
                    </label>
                    <label className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 cursor-pointer transition-colors ${
                      !form.es_electronica ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input
                        type="radio"
                        checked={!form.es_electronica}
                        onChange={() => setForm(prev => ({ ...prev, es_electronica: false }))}
                        className="sr-only"
                      />
                      <span className={!form.es_electronica ? 'text-amber-700 font-medium' : 'text-gray-600'}>
                        Física (B)
                      </span>
                    </label>
                  </div>
                </div>
              </>
            )}

            {/* Prefijo generado */}
            {form.prefijo && !esEdicion && (
              <div className="bg-gray-50 rounded-xl p-3 text-center">
                <span className="text-sm text-gray-500">Prefijo:</span>
                <span className="ml-2 font-mono text-xl font-bold text-gray-800">{form.prefijo}</span>
              </div>
            )}

            {/* Info de secuencia en uso */}
            {esEdicion && enUso && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm">
                <p className="font-medium text-amber-800 mb-1">Secuencia en uso</p>
                <p className="text-amber-700">
                  Contador actual: <span className="font-mono">{form.contador?.toLocaleString()}</span>
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  El contador y prefijo no se pueden modificar.
                </p>
              </div>
            )}

            {/* Contador Inicial - solo para nuevas */}
            {!esEdicion && (
              <div>
                <label className="block text-sm text-gray-600 mb-1">Contador Inicial *</label>
                <input
                  type="number"
                  value={form.contador}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    setForm(prev => ({ ...prev, contador: val >= 1 ? val : 1 }));
                  }}
                  min="1"
                  className="w-full border border-gray-300 rounded-xl px-3 py-2"
                />
                <p className="text-xs text-gray-400 mt-1">Desde qué número comenzará la secuencia (mínimo 1)</p>
              </div>
            )}

            {/* Fin de Secuencia */}
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Fin de Secuencia * 
                {esEdicion && <span className="text-gray-400 ml-1">(mín: {form.contador?.toLocaleString()})</span>}
              </label>
              <input
                type="number"
                value={form.fin}
                onChange={(e) => {
                  const val = e.target.value;
                  setForm(prev => ({ ...prev, fin: val === '' ? '' : (parseInt(val) || 0) }));
                }}
                min={form.contador || 1}
                className="w-full border border-gray-300 rounded-xl px-3 py-2"
                required
              />
            </div>

            {/* Fecha de Vencimiento */}
            <div>
              <label className="block text-sm text-gray-600 mb-1">Fecha de Vencimiento *</label>
              <input
                type="date"
                value={form.fecha_vencimiento}
                onChange={(e) => setForm(prev => ({ ...prev, fecha_vencimiento: e.target.value }))}
                min={new Date().toISOString().split('T')[0]}
                className="w-full border border-gray-300 rounded-xl px-3 py-2"
                required
              />
            </div>

            {/* Autorización y Alerta */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Autorización DGII</label>
                <input
                  type="text"
                  value={form.autorizacion}
                  onChange={(e) => setForm(prev => ({ ...prev, autorizacion: e.target.value }))}
                  placeholder="Opcional"
                  className="w-full border border-gray-300 rounded-xl px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Alertar cuando falten</label>
                <input
                  type="number"
                  value={form.alerta}
                  onChange={(e) => {
                    const val = e.target.value;
                    setForm(prev => ({ ...prev, alerta: val === '' ? '' : (parseInt(val) || 0) }));
                  }}
                  min="0"
                  className="w-full border border-gray-300 rounded-xl px-3 py-2"
                />
              </div>
            </div>

            {/* Botones */}
            <div className="flex gap-3 pt-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={guardando}
                className="flex-1 px-4 py-2 bg-color-primario text-white rounded-xl hover:bg-color-secundario disabled:opacity-50"
              >
                {guardando ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      </PlantillaModal>

      <ModalConfirmacion
        mostrar={mostrarConfirmacion}
        titulo={esEdicion ? "¿Guardar cambios?" : "¿Crear secuencia?"}
        mensaje={esEdicion 
          ? `Se actualizará la secuencia ${form.prefijo}.`
          : `Se creará una nueva secuencia ${form.prefijo} desde ${form.contador?.toLocaleString()} hasta ${form.fin?.toLocaleString()}.`
        }
        onConfirmar={confirmarGuardar}
        onCancelar={() => setMostrarConfirmacion(false)}
      />
    </>
  );
}

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================
function MantenimientoSecuenciaComprobante() {
  const [cargando, setCargando] = useState(true);
  const [tab, setTab] = useState('activas');
  const [tipos, setTipos] = useState([]);
  const [gruposActivos, setGruposActivos] = useState([]);
  const [secuenciasArchivo, setSecuenciasArchivo] = useState([]);
  const [archivoYaCargado, setArchivoYaCargado] = useState(false);
  const [cargandoArchivo, setCargandoArchivo] = useState(false);
  const [expandidos, setExpandidos] = useState({});
  const [mostrarModal, setMostrarModal] = useState(false);
  const [secuenciaEditando, setSecuenciaEditando] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [confirmacion, setConfirmacion] = useState({ mostrar: false, secuencia: null, nuevoEstado: null });
  const [filtroSerie, setFiltroSerie] = useState('todas'); // 'todas', 'electronica', 'fisica'

  // Cargar datos iniciales
  const cargarDatos = useCallback(async () => {
    try {
      setCargando(true);
      const [tiposRes, agrupadasRes] = await Promise.all([
        instanciaAxios.get('/comprobantefiscal/tipo'),
        instanciaAxios.get('/comprobantefiscal/secuencia/agrupadas')
      ]);

      setTipos(tiposRes.data || []);
      setGruposActivos(agrupadasRes.data || []);
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al cargar:', error);
      toast.error('Error al cargar datos');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // Lazy loading del archivo
  const cargarArchivo = async () => {
    if (archivoYaCargado) return;
    
    try {
      setCargandoArchivo(true);
      const res = await instanciaAxios.get('/comprobantefiscal/secuencia/archivo');
      setSecuenciasArchivo(res.data || []);
      setArchivoYaCargado(true);
    } catch (error) {
      if (esErrorPermiso(error)) return;
      toast.error('Error al cargar archivo');
    } finally {
      setCargandoArchivo(false);
    }
  };

  const handleTabArchivo = () => {
    setTab('archivo');
    cargarArchivo();
  };

  // Filtrar grupos por búsqueda
  const gruposFiltrados = gruposActivos.filter(g => {
    if (!busqueda) return true;
    const termino = busqueda.toLowerCase();
    return g.prefijo.toLowerCase().includes(termino) || 
           g.tipo_descripcion?.toLowerCase().includes(termino);
  });

  // Filtrar grupos por tipo de serie
  const gruposFiltradosPorSerie = gruposFiltrados.filter(g => {
    if (filtroSerie === 'todas') return true;
    if (filtroSerie === 'electronica') return g.es_electronica;
    if (filtroSerie === 'fisica') return !g.es_electronica;
    return true;
  });

  // Calcular SERIES sin configurar (cada serie E y B por separado)
  const prefijosConfigurados = gruposActivos.map(g => g.prefijo);
  const seriesSinConfigurar = [];
  
  tipos.forEach(tipo => {
    // Serie electrónica
    if (tipo.serie_electronica && !prefijosConfigurados.includes(tipo.serie_electronica)) {
      seriesSinConfigurar.push({
        tipo,
        serie: tipo.serie_electronica,
        esElectronica: true
      });
    }
    // Serie física (excepto B12 que es solo físico y no tiene electrónico)
    if (tipo.serie_fisica && !prefijosConfigurados.includes(tipo.serie_fisica)) {
      seriesSinConfigurar.push({
        tipo,
        serie: tipo.serie_fisica,
        esElectronica: false
      });
    }
  });

  // Filtrar series sin configurar por tipo de serie
  const seriesSinConfigurarFiltradas = seriesSinConfigurar.filter(s => {
    if (filtroSerie === 'todas') return true;
    if (filtroSerie === 'electronica') return s.esElectronica;
    if (filtroSerie === 'fisica') return !s.esElectronica;
    return true;
  });

  const handleGuardar = async (form) => {
    try {
      await instanciaAxios.post('/comprobantefiscal/secuencia/individual', form);
      toast.success(form.id_secuencia_comprobante ? 'Secuencia actualizada' : 'Secuencia creada');
      cargarDatos();
      // Resetear archivo para que se recargue
      setArchivoYaCargado(false);
    } catch (error) {
      if (esErrorPermiso(error)) throw error;
      toast.error(error.response?.data?.mensaje || 'Error al guardar');
      throw error;
    }
  };

  const handleEditar = (secuencia) => {
    setSecuenciaEditando(secuencia);
    setMostrarModal(true);
  };

  const handleConfigurarTipo = (tipo, serie, esElectronica) => {
    setSecuenciaEditando({
      id_tipo_comprobante_fiscal: tipo.id_tipo_comprobante_fiscal,
      es_electronica: esElectronica,
      prefijo: serie
    });
    setMostrarModal(true);
  };

  const solicitarCambioEstado = (secuencia, nuevoEstado) => {
    setConfirmacion({ mostrar: true, secuencia, nuevoEstado });
  };

  const confirmarCambioEstado = async () => {
    const { secuencia, nuevoEstado } = confirmacion;
    setConfirmacion({ mostrar: false, secuencia: null, nuevoEstado: null });
    
    try {
      await instanciaAxios.patch(`/comprobantefiscal/secuencia/${secuencia.id_secuencia_comprobante}/estado`, { 
        estado: nuevoEstado 
      });
      toast.success('Estado actualizado');
      cargarDatos();
      setArchivoYaCargado(false);
    } catch (error) {
      if (esErrorPermiso(error)) return;
      toast.error(error.response?.data?.mensaje || 'Error al cambiar estado');
    }
  };

  const toggleExpand = (prefijo) => {
    setExpandidos(prev => ({ ...prev, [prefijo]: !prev[prefijo] }));
  };

  if (cargando) {
    return <SpinnerCargando />;
  }

  return (
    <div className="min-h-screen bg-gray-200 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="font-cascadia text-xl font-semibold text-gray-800">Secuencias NCF</h1>
            <p className="text-sm text-gray-500">Gestione sus secuencias de comprobantes fiscales</p>
          </div>
          <button
            onClick={() => { setSecuenciaEditando(null); setMostrarModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-color-primario text-white rounded-xl hover:bg-color-secundario transition-colors"
          >
            <FiPlus size={16} />
            Nueva Secuencia
          </button>
        </div>

        {/* Tabs y Búsqueda */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-4">
          <div className="flex justify-between items-center p-3 border-b border-gray-100">
            <div className="flex gap-1">
              <button
                onClick={() => setTab('activas')}
                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                  tab === 'activas' 
                    ? 'bg-color-primario text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Activas ({gruposActivos.length})
              </button>
              <button
                onClick={handleTabArchivo}
                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                  tab === 'archivo' 
                    ? 'bg-color-primario text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Archivo {archivoYaCargado && `(${secuenciasArchivo.length})`}
              </button>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Filtro E/B */}
              <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
                <button
                  onClick={() => setFiltroSerie('todas')}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                    filtroSerie === 'todas' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500'
                  }`}
                >
                  Todas
                </button>
                <button
                  onClick={() => setFiltroSerie('electronica')}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                    filtroSerie === 'electronica' ? 'bg-blue-500 text-white' : 'text-gray-500'
                  }`}
                >
                  E
                </button>
                <button
                  onClick={() => setFiltroSerie('fisica')}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                    filtroSerie === 'fisica' ? 'bg-amber-500 text-white' : 'text-gray-500'
                  }`}
                >
                  B
                </button>
              </div>
              
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar..."
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg w-48"
              />
            </div>
          </div>

          {/* Contenido Tab Activas */}
          {tab === 'activas' && (
            <div>
              {gruposFiltradosPorSerie.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  {filtroSerie === 'todas' 
                    ? 'No hay secuencias activas' 
                    : `No hay secuencias ${filtroSerie === 'electronica' ? 'electrónicas' : 'físicas'} activas`
                  }
                </div>
              ) : (
                gruposFiltradosPorSerie.map(grupo => (
                  <FilaGrupo
                    key={grupo.prefijo}
                    grupo={grupo}
                    expandido={expandidos[grupo.prefijo]}
                    onToggle={() => toggleExpand(grupo.prefijo)}
                    onEditar={handleEditar}
                    onCambiarEstado={solicitarCambioEstado}
                  />
                ))
              )}
            </div>
          )}

          {/* Contenido Tab Archivo */}
          {tab === 'archivo' && (
            <div>
              {cargandoArchivo ? (
                <div className="p-8 text-center text-gray-400">Cargando...</div>
              ) : secuenciasArchivo.length === 0 ? (
                <div className="p-8 text-center text-gray-400">No hay secuencias archivadas</div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {secuenciasArchivo.map(sec => (
                    <div key={sec.id_secuencia_comprobante} className="flex items-center justify-between px-4 py-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-medium">{sec.prefijo}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            sec.id_estado === 2 ? 'bg-red-100 text-red-700' :
                            sec.id_estado === 3 ? 'bg-gray-200 text-gray-600' :
                            sec.id_estado === 4 ? 'bg-gray-100 text-gray-500' :
                            'bg-red-50 text-red-600'
                          }`}>
                            {sec.estado_texto}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">
                          Rango usado: {sec.rango_usado}
                        </p>
                      </div>
                      {sec.id_estado === ESTADOS.INACTIVA && (
                        <button
                          onClick={() => solicitarCambioEstado(sec, ESTADOS.ACTIVA)}
                          className="text-sm text-color-secundario hover:text-color-primario"
                        >
                          Reactivar
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Series sin Configurar */}
        {tab === 'activas' && seriesSinConfigurarFiltradas.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm mt-4">
            <div className="px-4 py-3 border-b border-gray-100">
              <h2 className="text-sm font-medium text-gray-500 uppercase">
                Sin Configurar ({seriesSinConfigurarFiltradas.length})
              </h2>
            </div>
            {seriesSinConfigurarFiltradas.map(({ tipo, serie, esElectronica }) => (
              <SerieSinConfigurar
                key={`${tipo.id_tipo_comprobante_fiscal}-${serie}`}
                tipo={tipo}
                serie={serie}
                esElectronica={esElectronica}
                onConfigurar={handleConfigurarTipo}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal de edición */}
      <ModalEdicion
        mostrar={mostrarModal}
        onClose={() => { setMostrarModal(false); setSecuenciaEditando(null); }}
        secuencia={secuenciaEditando}
        tipos={tipos}
        onGuardar={handleGuardar}
      />

      {/* Modal de confirmación */}
      <ModalConfirmacion
        mostrar={confirmacion.mostrar}
        titulo="Confirmar cambio"
        mensaje={`¿${confirmacion.nuevoEstado === ESTADOS.ACTIVA ? 'Reactivar' : 'Desactivar'} esta secuencia?`}
        onConfirmar={confirmarCambioEstado}
        onCancelar={() => setConfirmacion({ mostrar: false, secuencia: null, nuevoEstado: null })}
        peligroso={confirmacion.nuevoEstado !== ESTADOS.ACTIVA}
      />
    </div>
  );
}

export default MantenimientoSecuenciaComprobante;
